<script setup lang="ts">
import { APP_NAME } from "@/assets/js/appConfig";
import { marked } from "marked";

definePage({
	name: "CookiePolicyPage",
});
const CONTACT_EMAIL = "contact@olderone.com";

const privacyPolicyMarkdown = `
# Cookie Policy

**Effective Date:** July 2, 2026

This Cookie Policy explains how **${APP_NAME}** uses cookies and similar technologies on our website.

## 1. What Are Cookies

Cookies are small text files stored on your device when you visit a website. Some cookies are necessary for a website to function properly.

## 2. Cookies We Use

We only use **strictly necessary cookies** and similar technologies required for:

- website security
- core technical functionality
- load balancing, session integrity, or essential site behavior
- remembering limited technical preferences necessary for operation

We do **not** use cookies for:

- advertising
- behavioral profiling
- optional analytics
- account login features

## 3. Legal Basis / Consent

Because these cookies are strictly necessary for the operation of the website, they may be used without consent where allowed by applicable law.

However, we still explain their use in this Cookie Policy for transparency.

## 4. Local and Session Storage

The website may use local or session storage for essential preferences, temporary game state, and basic technical functionality.

This storage is used only where needed for the website to work properly and may remain on your device until it expires or you clear it from your browser.

## 5. Managing Cookies

Most browsers allow you to block or delete cookies. Please note that disabling strictly necessary cookies may affect the proper functioning of the website.

## 6. Changes to This Policy

We may update this Cookie Policy from time to time. Any changes will be posted on this page with a revised effective date.

## 7. Contact

If you have any questions about this Cookie Policy, contact:

**${CONTACT_EMAIL}**
`;

const privacyPolicyHtml = computed(() => marked.parse(privacyPolicyMarkdown));
</script>

<template>
	<section class="legal-page">
		<div class="overlay"></div>

		<RouterLink
			to="/"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<div class="legal-card">
			<div
				class="markdown-body"
				v-html="privacyPolicyHtml"
			></div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.legal-page {
	position: relative;
	min-height: 100dvh;
	padding: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background:
		radial-gradient(circle at center, #2a2a2a 0%, #171717 46%, #070707 100%),
		#101010;
	// background-image: url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
		);
	z-index: 0;
}
.legal-card {
	position: relative;
	z-index: 1;
	width: 100%;
	max-width: 920px;
	max-height: calc(100dvh - 40px);
	overflow-y: auto;
	overflow-x: hidden;
	padding: 28px 24px;
	border-radius: 24px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.18) transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.16);
		border-radius: 999px;
		border: 2px solid transparent;
		background-clip: padding-box;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: rgba(255, 255, 255, 0.24);
		border: 2px solid transparent;
		background-clip: padding-box;
	}
}

.markdown-body {
	color: rgba(255, 255, 255, 0.9);
	line-height: 1.7;
	font-size: 15px;

	:deep(h1) {
		margin: 0 0 20px;
		font-size: clamp(32px, 5vw, 48px);
		line-height: 1;
		font-weight: 900;
		letter-spacing: -0.03em;
		color: $text-color-white;
	}

	:deep(h2) {
		margin: 28px 0 12px;
		font-size: 22px;
		font-weight: 800;
		color: $text-color-white;
	}

	:deep(p) {
		margin: 0 0 14px;
	}

	:deep(ul) {
		margin: 0 0 16px 18px;
		padding: 0;
	}

	:deep(li) {
		margin-bottom: 8px;
	}

	:deep(strong) {
		color: $text-color-white;
		font-weight: 800;
	}

	:deep(a) {
		color: $text-color-white;
	}
}

@media (max-width: 640px) {
	.legal-page {
		align-items: flex-start;
		padding: 76px 16px calc(24px + env(safe-area-inset-bottom));
		overflow-y: auto;
	}

	.legal-card {
		max-height: none;
		padding: 22px 18px;
		border-radius: 20px;
		overflow: visible;
	}

	.markdown-body {
		font-size: 14px;

		:deep(h2) {
			font-size: 20px;
		}
	}
}
</style>
